import { Draggable } from '@hello-pangea/dnd';
import { Add, GripVertical, IconComponent } from '@/components/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface DraggableToolboxItemProps {
    id: string;
    index: number;
    icon: IconComponent;
    label: string;
    description?: string;
    /** What the block is and how to place it. Shown on hover; needs a
     *  `TooltipProvider` above this component. */
    tooltip?: string;
    onClick?: () => void;
    disabled?: boolean;
    /**
     * The tool has no meaningful "insert at the end" — only a position chosen
     * by hand. Drops the `+`, which is the glyph that promises a click. The
     * card itself stays identical to the others: a second visual treatment for
     * one card read as "broken" rather than "different", and the missing `+`
     * plus the tooltip already carry the difference.
     */
    dragOnly?: boolean;
    className?: string;
}

export default function DraggableToolboxItem({
    id,
    index,
    icon: Icon,
    label,
    description,
    tooltip,
    onClick,
    disabled = false,
    dragOnly = false,
    className,
}: DraggableToolboxItemProps) {
    return (
        <Draggable draggableId={id} index={index} isDragDisabled={disabled}>
            {(provided, snapshot) => {
                const card = (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={cn(
                            'group/tool rounded-2xl border-2 border-border bg-card p-4 transition-all',
                            disabled
                                ? 'cursor-not-allowed opacity-50 grayscale'
                                : 'cursor-grab hover:bg-surface active:cursor-grabbing',
                            snapshot.isDragging && 'rotate-2 opacity-80 ring-2 ring-blue-500',
                            className,
                        )}
                        onClick={!disabled && !dragOnly ? onClick : undefined}
                        style={provided.draggableProps.style}
                    >
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex min-w-0 items-center gap-2">
                                {/* Faint at rest so it reads as texture, legible the
                                    moment the pointer is on the card it belongs to. */}
                                <GripVertical
                                    className={cn(
                                        'size-4 shrink-0 transition-colors group-hover/tool:text-muted-foreground',
                                        snapshot.isDragging
                                            ? 'text-muted-foreground'
                                            : 'text-border-strong',
                                    )}
                                />
                                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-surface text-muted-foreground">
                                    <Icon className="size-5" />
                                </div>
                                <div className="min-w-0 text-left">
                                    <h4 className="text-sm font-medium leading-snug text-foreground">
                                        {label}
                                    </h4>
                                    {description && (
                                        <p className="text-xs text-muted-foreground">
                                            {description}
                                        </p>
                                    )}
                                </div>
                            </div>
                            {/* Nothing stands where the `+` would be: the tooltip
                                is what gives that absence a meaning. */}
                            {!dragOnly && <Add className="size-4 shrink-0 text-muted-foreground" />}
                        </div>
                    </div>
                );

                if (!tooltip) return card;

                return (
                    <Tooltip>
                        <TooltipTrigger asChild>{card}</TooltipTrigger>
                        {/* Out toward the laudo: the panel sits on the right, so
                            that is the side with empty space next to it. In the
                            grid's second column the tooltip still lands on the
                            card beside it, which is why it goes passive —
                            `data-passive-tooltip` drops the hit area of Radix's
                            wrapper as well, so the covered card stays draggable
                            underneath it. */}
                        <TooltipContent
                            side="left"
                            sideOffset={8}
                            data-passive-tooltip=""
                            className="pointer-events-none max-w-64 leading-relaxed"
                        >
                            {tooltip}
                        </TooltipContent>
                    </Tooltip>
                );
            }}
        </Draggable>
    );
}
